type RequestHandlerOptions = Omit<
  RequestInit,
  "credentials" | "method" | "body"
> & {
  method?: "GET" | "POST" | "PUT" | "DELETE"
  body?: Record<string, string | number | boolean>
  queryParams?: Record<string, string | undefined>
}

type Endpoint = `/${string}`

type PassportIdentityProvider = "discord"

type PassportAccount = {
  app_metadata: {
    providers: PassportIdentityProvider[]
  }
  user_metadata: {
    btcAddress: string
    evmAddress: string
  }
}

const PassportError = (message: string) => new Error(`[Passport] ${message}`)

class Passport {
  private baseUrl: string

  private apiRoute: string

  constructor(apiEndpoint: string) {
    const url = new URL(apiEndpoint.replace(/\/$/, ""))
    this.baseUrl = url.origin
    this.apiRoute = url.pathname
  }

  /**
   * The request handler for the Passport API. It accepts an API endpoint and request options.
   * @dev Generic types `R` and `B` are used to define the response and request body types, respectively.
   * @param {Endpoint} endpoint - The API endpoint to request
   * @param {RequestHandlerOptions<B>} options - The request options, the `RequestInit` type with additional type-safe properties
   * @returns The promise of the API response
   */
  private async handleRequest<D>(
    endpoint: Endpoint,
    options: RequestHandlerOptions,
  ): Promise<D> {
    const {
      method,
      headers,
      body,
      queryParams = {},
      ...restOptions
    } = options || {}

    const sanitizedQueryParams = Object.fromEntries(
      Object.entries(queryParams).filter(([, value]) => value),
    ) as Record<string, string>

    const apiEndpoint = [this.apiRoute, endpoint].join("")

    const endpointWithParams = [
      apiEndpoint,
      new URLSearchParams(sanitizedQueryParams).toString(),
    ]
      .filter(Boolean)
      .join("?")

    const requestUrl = new URL(endpointWithParams, this.baseUrl)

    try {
      const response = await fetch(requestUrl, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        ...restOptions,
      })

      const data = await response.json()

      if (!response.ok) {
        const error = data?.error || "An error occurred"
        throw PassportError(error)
      }

      return data as D
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred"

      throw PassportError(errorMessage)
    }
  }

  /**
   * Creates a session for the Passport account.
   * @param {PassportCredentials} credentials - The Passport credentials to create a session
   * @returns {PassportAccount} The Passport account
   */
  async createSession(type: PassportIdentityProvider | "wallet") {
    const { user: account } = await this.handleRequest<{
      user: PassportAccount
    }>("/session", {
      method: "POST",
      body: { type },
    })

    return account
  }

  /**
   * Retrieved the session from the code provided by indentity provider.
   * @param {string} code - The code provided by the identity provider
   * @returns {PassportAccount} The Passport account
   */
  async getSession(code?: string) {
    const { user: account } = await this.handleRequest<{
      user: PassportAccount
    }>("/session", {
      method: "GET",
      queryParams: { code },
    })

    return account
  }

  /**
   * Gets a Passport account accordingly to the existing session.
   * @returns {PassportAccount} The Passport account
   * @dev If account doesn't exist, the new one will be created.
   */
  async getAccount() {
    const account = await this.handleRequest<PassportAccount>("/accounts", {
      method: "POST",
    })

    return account
  }

  /**
   * Links a Passport account with a provider.
   * @param {PassportIdentityProvider} provider - The Passport identity provider to link
   * @param {string} fallbackAddress - The Passport account address, fallback to the account's address
   * @returns {PassportAccount | { redirectTo: string }} The Passport account or redirect URL
   */
  async linkAccount(provider: PassportIdentityProvider) {
    const endpoint: Endpoint = "/account/linked-accounts"

    return this.handleRequest<PassportAccount | { redirectTo: string }>(
      endpoint,
      {
        method: "POST",
        body: { type: provider },
      },
    )
  }

  /**
   * Checks if a Passport account has a linked provider account.
   * @param {PassportIdentityProvider} provider - The Passport identity provider to check
   * @returns {boolean} The Passport account has a linked provider account
   */
  async hasLinkedProviderAccount(provider: PassportIdentityProvider) {
    const account = await this.getAccount()

    const linkedProviders = account?.app_metadata?.providers || []

    return linkedProviders.includes(provider)
  }
}

const passport = new Passport(process.env.API_ENDPOINT_V2)

export default passport
